// Dashboard talks to the same Hono server that serves it (same-origin in
// production; Vite dev proxy in development). Auth is the session cookie set
// by the OAuth callback — no Bearer token from the browser, so we send the
// cookie via `credentials: "include"`. Marked caller-auth failures are handled
// here so every query and mutation shares one session-expiry transition.

import {
  createApiFetch,
  isCallerAuthFailure,
  routes,
  type ApiFetch,
  type RequestOptions,
} from "@nova-ai/shared"

const sharedApiFetch = createApiFetch({
  baseUrl: "",
  bearerToken: "",
  credentials: "include",
})

type CallerAuthFailureHandler = () => void | Promise<void>

let callerAuthFailureHandler: CallerAuthFailureHandler | null = null
let callerAuthTransition: Promise<void> | null = null
let sessionAbortController = new AbortController()

export const apiFetch: ApiFetch = async <ResponseBody>(
  path: string,
  options: RequestOptions = {}
) => {
  const signal = options.signal
    ? AbortSignal.any([options.signal, sessionAbortController.signal])
    : sessionAbortController.signal
  const result = await sharedApiFetch<ResponseBody>(path, { ...options, signal })

  if (
    !result.ok &&
    path !== routes.auth.me() &&
    isCallerAuthFailure(result.error) &&
    callerAuthFailureHandler
  ) {
    if (!callerAuthTransition) {
      sessionAbortController.abort(new Error("Dashboard session expired"))
      callerAuthTransition = Promise.resolve(callerAuthFailureHandler())
    }
    await callerAuthTransition
  }

  return result
}

export function configureCallerAuthFailureHandler(handler: CallerAuthFailureHandler): () => void {
  callerAuthFailureHandler = handler
  callerAuthTransition = null
  sessionAbortController = new AbortController()

  return () => {
    if (callerAuthFailureHandler === handler) {
      callerAuthFailureHandler = null
      callerAuthTransition = null
      sessionAbortController = new AbortController()
    }
  }
}
